import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import { Box, Alert, CircularProgress, Divider, Tabs, Tab } from '@mui/material';
import { useNotification } from '../../../utils/useNotification';
import {
  Security,
  PlayCircleOutline,
  History as HistoryIcon,
  SmartToy
} from '@mui/icons-material';
import QuestionAIChat from '../../../components/Forum/QuestionAIChat';
import TabPanel from './components/TabPanel';
import QuestionHeader from './components/QuestionHeader';
import CodeWorkspace from './components/CodeWorkspace/CodeWorkspace';
import PublicTestEditor from './components/TestCasesPanel/PublicTestEditor';
import SecretTestEditor from './components/TestCasesPanel/SecretTestEditor';
import SubmissionsPanel from './components/SubmissionsPanel/SubmissionsPanel';
import {
  useQuestion,
  useCodeExecution,
  useSubmissions,
  useTestCases
} from './hooks';

/**
 * 题目详情页面
 * 重构版本 - 使用提取的 hooks 和组件
 */
function QuestionDetail() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const isTeacher = user?.role === 'teacher';
  const { showPointsNotification, NotificationComponent } = useNotification();

  // Hooks
  const { question, loading, error, updateQuestion, saveDifficulty, getEditorLanguage, getInitialCode } = useQuestion(id);
  const { submissions, refetch: refetchSubmissions, addSubmission } = useSubmissions(id);
  const {
    running,
    executing,
    submitting,
    runResult,
    executeResult,
    result,
    runCode,
    executeCode,
    submitCode
    // clearResults // Reserved for future clear results feature
  } = useCodeExecution(question, id);
  const {
    editingPublicTest,
    editingSecretTest,
    publicTestCode,
    secretTestCode,
    savingTest,
    setPublicTestCode,
    setSecretTestCode,
    handleEditPublicTest,
    handleCancelPublicTest,
    handleEditSecretTest,
    handleCancelSecretTest,
    savePublicTest,
    saveSecretTest
  } = useTestCases(question, id, isTeacher);

  // Local state
  const [code, setCode] = useState('');
  const [rightTab, setRightTab] = useState(0); // 0: Public Test, 1: Secret Test (teacher only), 2/1: AI Chat, 3/2: Submissions
  const [timerState, setTimerState] = useState({ running: false, elapsedTime: 0 });

  // Initialize code when question loads
  useEffect(() => {
    if (question) {
      const initialCode = getInitialCode();
      setCode(initialCode);
    }
  }, [question, getInitialCode]);

  // Handle code execution
  const handleExecute = async () => {
    try {
      await executeCode(code);
      // Switch to Output tab (handled by CodeWorkspace internally)
      setRightTab(isTeacher ? 1 : 0); // Switch to Public Test tab
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle code submission
  const handleSubmit = async () => {
    try {
      const submission = await submitCode(code);
      addSubmission(submission);
      refetchSubmissions(); // Refresh submissions list
      // Switch to submissions tab
      setRightTab(getSubmissionsTabIndex());
      // Stop timer if running
      if (timerState.running) {
        setTimerState({ ...timerState, running: false });
      }
      // Show points notification if submission passed (only for students)
      if (submission.status === 'passed' && submission.score > 0 && !isTeacher) {
        showPointsNotification(submission.score);
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle running with test cases
  const handleRun = async (testType = 'public') => {
    try {
      await runCode(code, testType);
      // Switch to Test Result tab (handled by CodeWorkspace internally)
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle saving test cases
  const handleSavePublicTest = async () => {
    try {
      const updatedQuestion = await savePublicTest();
      if (updatedQuestion) {
        updateQuestion(updatedQuestion);
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSaveSecretTest = async () => {
    try {
      const updatedQuestion = await saveSecretTest();
      if (updatedQuestion) {
        updateQuestion(updatedQuestion);
      }
    } catch (error) {
      // Error handled by hook
    }
  };

  // Handle reset code
  const handleResetCode = (templateCode) => {
    setCode(templateCode);
  };

  // Calculate right tab indices based on user role
  // Teacher: [Public Test (0), Secret Test (1), AI Chat (2), Submissions (3)]
  // Student: [Public Test (0), AI Chat (1), Submissions (2)]
  const enableAI = question?.enableAI !== false;
  const getAIChatTabIndex = () => {
    if (!enableAI) return -1;
    return isTeacher ? 2 : 1;
  };
  const getSubmissionsTabIndex = () => {
    if (!enableAI) {
      return isTeacher ? 2 : 1;
    }
    return isTeacher ? 3 : 2;
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !question) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || '题目未找到'}</Alert>
      </Box>
    );
  }

  const editorLanguage = getEditorLanguage();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'background.default',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      {/* Header */}
      <QuestionHeader
        question={question}
        user={user}
        executing={executing}
        submitting={submitting}
        onExecute={handleExecute}
        onSubmit={handleSubmit}
        onTimerChange={setTimerState}
        isTeacher={isTeacher}
        onDifficultyChange={saveDifficulty}
      />

      <Divider />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Side - Code Workspace */}
        <CodeWorkspace
          language={editorLanguage}
          code={code}
          onCodeChange={setCode}
          executeResult={executeResult}
          runResult={runResult}
          onResetCode={handleResetCode}
          userRole={user?.role || 'student'}
          question={question}
        />

        {/* Right Side - Tabs */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
          {/* Right Side Tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
              height: 48,
              display: 'flex',
              alignItems: 'flex-end'
            }}
          >
            <Tabs value={rightTab} onChange={(e, v) => setRightTab(v)} sx={{ minHeight: 48, height: 48 }}>
              <Tab
                label="Public Test"
                icon={<PlayCircleOutline fontSize="small" />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              {isTeacher && (
                <Tab
                  label="Secret Test"
                  icon={<Security fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
              )}
              {question?.enableAI !== false && (
                <Tab
                  label="AI 助手"
                  icon={<SmartToy fontSize="small" />}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
              )}
              <Tab
                label="Submissions"
                icon={<HistoryIcon fontSize="small" />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          {/* Public Test Tab */}
          <TabPanel value={rightTab} index={0}>
            <PublicTestEditor
              question={question}
              testCode={publicTestCode}
              onTestCodeChange={setPublicTestCode}
              editing={editingPublicTest}
              onEdit={handleEditPublicTest}
              onSave={handleSavePublicTest}
              onCancel={handleCancelPublicTest}
              onRun={handleRun}
              running={running}
              saving={savingTest}
              isTeacher={isTeacher}
            />
          </TabPanel>

          {/* Secret Test Tab - Teacher Only */}
          {isTeacher && (
            <TabPanel value={rightTab} index={1}>
              <SecretTestEditor
                question={question}
                testCode={secretTestCode}
                onTestCodeChange={setSecretTestCode}
                editing={editingSecretTest}
                onEdit={handleEditSecretTest}
                onSave={handleSaveSecretTest}
                onCancel={handleCancelSecretTest}
                onRun={handleRun}
                running={running}
                saving={savingTest}
              />
            </TabPanel>
          )}

          {/* AI Chat Tab - Only show if AI is enabled */}
          {enableAI && getAIChatTabIndex() >= 0 && (
            <TabPanel value={rightTab} index={getAIChatTabIndex()}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <QuestionAIChat questionId={id} />
              </Box>
            </TabPanel>
          )}

          {/* Submissions Tab */}
          <TabPanel value={rightTab} index={getSubmissionsTabIndex()}>
            <SubmissionsPanel submissions={submissions} latestResult={result} />
          </TabPanel>
        </Box>
      </Box>
      
      <NotificationComponent />
    </Box>
  );
}

export default QuestionDetail;
